import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple in-memory cache per tab session (complement to backend MongoDB cache)
const memoryCache = new Map(); // key = `${lang}::${text}` → translated

/**
 * useTranslatedTexts – translate a list of Swedish strings into the user's language.
 * Returns [translatedArray, { loading }]. Pass-through if already Swedish.
 */
export function useTranslatedTexts(texts = []) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "sv").split("-")[0];
  const [translated, setTranslated] = useState(texts);
  const [loading, setLoading] = useState(false);

  // Serialize inputs as a stable dependency key
  const key = useMemo(() => `${lang}::${JSON.stringify(texts)}`, [lang, texts]);

  useEffect(() => {
    if (lang === "sv" || texts.length === 0) {
      setTranslated(texts);
      return;
    }
    // Check memory cache first
    const cached = texts.map(t => memoryCache.get(`${lang}::${t}`));
    if (cached.every(c => c !== undefined)) {
      setTranslated(cached);
      return;
    }
    const toTranslate = texts.map((t, i) => ({ i, t }))
      .filter(o => memoryCache.get(`${lang}::${o.t}`) === undefined && o.t);

    if (toTranslate.length === 0) {
      setTranslated(cached.map((c, i) => c ?? texts[i]));
      return;
    }

    setLoading(true);
    axios
      .post(`${API}/translate/batch`, {
        texts: toTranslate.map(o => o.t),
        target_lang: lang,
      })
      .then(r => {
        const results = r.data?.results || [];
        toTranslate.forEach((o, idx) => {
          const result = results[idx];
          if (result && result.text) {
            memoryCache.set(`${lang}::${o.t}`, result.text);
          }
        });
        const newTranslated = texts.map(t => memoryCache.get(`${lang}::${t}`) ?? t);
        setTranslated(newTranslated);
      })
      .catch(() => {
        // On error, return originals
        setTranslated(texts);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [translated, { loading }];
}

/**
 * useTranslatedObjects – translate specific string fields in an array of objects.
 * Returns [translatedArray, { loading }].
 */
export function useTranslatedObjects(items = [], fields = []) {
  // Flatten all texts and keep index map
  const flatTexts = useMemo(() => {
    const arr = [];
    items.forEach(it => {
      fields.forEach(f => {
        arr.push(typeof it?.[f] === "string" ? it[f] : "");
      });
    });
    return arr;
  }, [items, fields]);

  const [translated, state] = useTranslatedTexts(flatTexts);

  const translatedItems = useMemo(() => {
    return items.map((it, idx) => {
      const copy = { ...it };
      fields.forEach((f, fi) => {
        const pos = idx * fields.length + fi;
        if (typeof it?.[f] === "string" && translated[pos]) {
          copy[f] = translated[pos];
        }
      });
      return copy;
    });
  }, [items, fields, translated]);

  return [translatedItems, state];
}
